import React, { useState, useEffect } from 'react';
import { useTraslados } from 'hooks/useTraslados';
import { useAuth } from 'contexts/AuthContext';
import { catalogoService } from 'services/usuarioService';
import { matriculaService } from 'services/matriculaService';
import {
  TrasladoResponse,
  TrasladoPayload,
  TIPO_TRASLADO_LABEL,
  ESTADO_TRASLADO_LABEL,
  ESTADO_TRASLADO_COLOR,
  TipoTraslado,
} from 'types/traslado.types';
import { MatriculaResponse } from 'types/matricula.types';
import { AñoEscolar } from 'types/usuario.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// TrasladosPage — Gestión de Traslados de estudiantes
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const FORM_INICIAL = {
  tipo: 'SALIDA' as TipoTraslado,
  ieNombre: '',
  ieCodigoModular: '',
  ieUgel: '',
  motivo: '',
  observacion: '',
};

interface AccionesFilaProps {
  traslado: TrasladoResponse;
  esAdminODirector: boolean;
  esDirector: boolean;
  acting: boolean;
  onAprobar: () => void;
  onRechazar: () => void;
  onEjecutar: () => void;
  onExonerar: () => void;
}

const AccionesFila: React.FC<AccionesFilaProps> = ({
  traslado, esAdminODirector, esDirector, acting,
  onAprobar, onRechazar, onEjecutar, onExonerar,
}) => {
  const { estado, tieneDeuda, deudaExonerada } = traslado;
  if (estado === 'RECHAZADO' || estado === 'EJECUTADO') {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex gap-1.5 flex-wrap">
      {estado === 'SOLICITADO' && esAdminODirector && (
        <>
          {esDirector && tieneDeuda && !deudaExonerada && (
            <button onClick={onExonerar} disabled={acting}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors disabled:opacity-60">
              Exonerar deuda
            </button>
          )}
          <button onClick={onAprobar} disabled={acting}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors disabled:opacity-60">
            Aprobar
          </button>
          <button onClick={onRechazar} disabled={acting}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors disabled:opacity-60">
            Rechazar
          </button>
        </>
      )}
      {estado === 'APROBADO' && esAdminODirector && (
        <button onClick={onEjecutar} disabled={acting}
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors disabled:opacity-60">
          Ejecutar
        </button>
      )}
    </div>
  );
};

const TrasladosPage: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.idUsuario ?? 0;
  const esAdminODirector = user?.rol === 'ADMIN' || user?.rol === 'DIRECTOR';
  const esDirector = user?.rol === 'DIRECTOR';

  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [anioId, setAnioId] = useState<number | null>(null);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [showNuevo, setShowNuevo] = useState(false);
  const [busquedaDni, setBusquedaDni] = useState('');
  const [matriculaEncontrada, setMatriculaEncontrada] = useState<MatriculaResponse | null>(null);
  const [buscandoMatricula, setBuscandoMatricula] = useState(false);
  const [busquedaError, setBusquedaError] = useState<string | null>(null);
  const [form, setForm] = useState(FORM_INICIAL);

  const [modalAccion, setModalAccion] = useState<{ traslado: TrasladoResponse; accion: 'aprobar' | 'rechazar' } | null>(null);
  const [obsAccion, setObsAccion] = useState('');
  const [showConfirmSalida, setShowConfirmSalida] = useState(false);

  const { traslados, loading, error, acting, actError, crear, aprobar, ejecutar, rechazar, exonerarDeuda } =
    useTraslados(anioId);

  useEffect(() => {
    const cargar = async () => {
      setCargandoCatalogos(true);
      try {
        const a = await catalogoService.getAñosEscolares();
        setAnios(a);
        if (a.length > 0) setAnioId(a[0].idAnioEscolar);
      } finally {
        setCargandoCatalogos(false);
      }
    };
    cargar();
  }, []);

  const handleBuscarMatricula = async () => {
    if (!busquedaDni.trim() || !anioId) return;
    setBuscandoMatricula(true);
    setBusquedaError(null);
    setMatriculaEncontrada(null);
    try {
      const lista = await matriculaService.listar({ anioEscolarId: anioId });
      const encontrada = lista.find(m => m.estudianteDni === busquedaDni.trim());
      if (encontrada) {
        setMatriculaEncontrada(encontrada);
      } else {
        setBusquedaError('No se encontró matrícula activa para ese DNI en el año escolar seleccionado');
      }
    } catch (e: unknown) {
      setBusquedaError(e instanceof Error ? e.message : 'Error al buscar matrícula');
    } finally {
      setBuscandoMatricula(false);
    }
  };

  const handleCrear = async () => {
    if (!matriculaEncontrada || !anioId) return;
    const payload: TrasladoPayload = {
      matriculaId: matriculaEncontrada.idMatricula,
      anioEscolarId: anioId,
      tipo: form.tipo,
      ieNombre: form.ieNombre,
      ieCodigoModular: form.ieCodigoModular,
      ieUgel: form.ieUgel,
      motivo: form.motivo,
      observacion: form.observacion || undefined,
    };
    await crear(payload, userId);
    setShowNuevo(false);
    setForm(FORM_INICIAL);
    setMatriculaEncontrada(null);
    setBusquedaDni('');
    setBusquedaError(null);
  };

  const handleConfirmarAccion = async () => {
    if (!modalAccion) return;
    if (modalAccion.accion === 'aprobar') {
      await aprobar(modalAccion.traslado.idTraslado, { aprobadorId: userId, observacion: obsAccion || undefined });
    } else {
      await rechazar(modalAccion.traslado.idTraslado, obsAccion || undefined);
    }
    setModalAccion(null);
    setObsAccion('');
  };

  const formatFecha = (f: string | null) =>
    f ? new Date(f).toLocaleDateString('es-PE') : '—';

  if (cargandoCatalogos) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Traslados</h1>
        <p className="text-blue-200 mt-1">Gestión de traslados de salida e ingreso de estudiantes</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Año Escolar</label>
          <select value={anioId ?? ''} onChange={e => setAnioId(Number(e.target.value))}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500">
            {anios.map(a => (
              <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowNuevo(true)}
          className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2.5 transition-colors">
          + Nuevo Traslado
        </button>
      </div>

      {(error || actError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          {error || actError}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><SpinnerIcon /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Estudiante', 'DNI', 'Tipo', 'IE', 'Motivo', 'Estado', 'F. Solicitud', 'Deuda', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {traslados.map(t => (
                  <tr key={t.idTraslado} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                        {t.estudianteApellidos}, {t.estudianteNombres}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{t.estudianteDni}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${t.tipo === 'SALIDA' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                        {TIPO_TRASLADO_LABEL[t.tipo]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[160px]">
                      <p className="truncate font-semibold" title={t.ieNombre}>{t.ieNombre}</p>
                      <p className="text-xs text-slate-400 truncate">{t.ieUgel}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[140px]">
                      <span className="truncate block" title={t.motivo}>{t.motivo}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_TRASLADO_COLOR[t.estado]}`}>
                        {ESTADO_TRASLADO_LABEL[t.estado]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatFecha(t.fechaSolicitud)}</td>
                    <td className="px-4 py-3">
                      {t.tieneDeuda && !t.deudaExonerada ? (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700">Deuda</span>
                      ) : t.deudaExonerada ? (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Exonerada</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <AccionesFila
                        traslado={t}
                        esAdminODirector={esAdminODirector}
                        esDirector={esDirector}
                        acting={acting}
                        onAprobar={() => { setModalAccion({ traslado: t, accion: 'aprobar' }); setObsAccion(''); }}
                        onRechazar={() => { setModalAccion({ traslado: t, accion: 'rechazar' }); setObsAccion(''); }}
                        onEjecutar={() => ejecutar(t.idTraslado)}
                        onExonerar={() => exonerarDeuda(t.idTraslado)}
                      />
                    </td>
                  </tr>
                ))}
                {traslados.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">
                      No hay traslados registrados para el año seleccionado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNuevo && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 flex-shrink-0">
              <h3 className="text-white font-bold">Nuevo Traslado</h3>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">DNI del Estudiante</label>
                <div className="flex gap-2">
                  <input type="text" maxLength={8} value={busquedaDni}
                    onChange={e => setBusquedaDni(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleBuscarMatricula()}
                    className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="12345678" />
                  <button onClick={handleBuscarMatricula} disabled={buscandoMatricula}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold transition-colors disabled:opacity-60">
                    {buscandoMatricula ? <SpinnerIcon /> : 'Buscar'}
                  </button>
                </div>
                {busquedaError && <p className="text-red-600 text-xs mt-1">{busquedaError}</p>}
                {matriculaEncontrada && (
                  <div className="mt-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-700 font-semibold">
                    {matriculaEncontrada.estudianteApellidos}, {matriculaEncontrada.estudianteNombres}
                    <span className="ml-2 text-xs font-normal text-green-600">
                      · {matriculaEncontrada.gradoNombre} {matriculaEncontrada.seccionNombre}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Tipo</label>
                <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value as TipoTraslado }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="SALIDA">Salida</option>
                  <option value="INGRESO">Ingreso</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                  {form.tipo === 'SALIDA' ? 'IE Destino' : 'IE Origen'}
                </label>
                <input type="text" value={form.ieNombre}
                  onChange={e => setForm(p => ({ ...p, ieNombre: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre de la institución educativa" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Código Modular</label>
                  <input type="text" value={form.ieCodigoModular}
                    onChange={e => setForm(p => ({ ...p, ieCodigoModular: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1234567" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">UGEL</label>
                  <input type="text" value={form.ieUgel}
                    onChange={e => setForm(p => ({ ...p, ieUgel: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="UGEL..." />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Motivo</label>
                <input type="text" value={form.motivo}
                  onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Motivo del traslado..." />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Observación (opcional)</label>
                <textarea rows={2} value={form.observacion}
                  onChange={e => setForm(p => ({ ...p, observacion: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Observaciones adicionales..." />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between flex-shrink-0">
              <button onClick={() => { setShowNuevo(false); setForm(FORM_INICIAL); setMatriculaEncontrada(null); setBusquedaDni(''); setBusquedaError(null); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
              <button
                onClick={() => form.tipo === 'SALIDA' ? setShowConfirmSalida(true) : handleCrear()}
                disabled={acting || !matriculaEncontrada || !form.motivo.trim() || !form.ieNombre.trim()}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl px-5 py-2 transition-colors disabled:opacity-60">
                {acting && <SpinnerIcon />}
                Registrar Traslado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmación traslado SALIDA ────────────────────── */}
      {showConfirmSalida && matriculaEncontrada && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Cabecera */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-base">Confirmar Traslado de Salida</h3>
                  <p className="text-orange-100 text-xs mt-0.5">
                    {matriculaEncontrada.estudianteApellidos}, {matriculaEncontrada.estudianteNombres}
                  </p>
                </div>
              </div>
            </div>

            {/* Cuerpo */}
            <div className="p-6 space-y-4">
              <p className="text-slate-700 text-sm">
                Estás registrando la <strong>salida</strong> del estudiante de la institución.
                Este traslado quedará en estado <strong>SOLICITADO</strong> hasta ser aprobado.
              </p>

              {/* Alerta de pensiones pendientes */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex gap-3">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                </svg>
                <div>
                  <p className="text-amber-800 text-sm font-semibold">Pensiones pendientes</p>
                  <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                    Las pensiones futuras del estudiante quedarán en estado <strong>PENDIENTE</strong>.
                    Una vez ejecutado el traslado, anúlalas manualmente desde el módulo de <strong>Cobros</strong>.
                  </p>
                </div>
              </div>

              <p className="text-slate-500 text-xs">
                IE destino: <span className="font-semibold text-slate-700">{form.ieNombre}</span>
                {form.ieUgel && <> · UGEL: <span className="font-semibold text-slate-700">{form.ieUgel}</span></>}
              </p>
            </div>

            {/* Pie */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setShowConfirmSalida(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
              <button
                onClick={async () => { setShowConfirmSalida(false); await handleCrear(); }}
                disabled={acting}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold rounded-xl px-5 py-2 transition-colors disabled:opacity-60">
                {acting && <SpinnerIcon />}
                Confirmar Salida
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAccion && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className={`px-6 py-4 ${modalAccion.accion === 'aprobar' ? 'bg-gradient-to-r from-green-700 to-green-600' : 'bg-gradient-to-r from-red-700 to-red-600'}`}>
              <h3 className="text-white font-bold">
                {modalAccion.accion === 'aprobar' ? 'Aprobar Traslado' : 'Rechazar Traslado'}
              </h3>
              <p className="text-white/70 text-xs mt-0.5">
                {modalAccion.traslado.estudianteApellidos}, {modalAccion.traslado.estudianteNombres}
              </p>
            </div>
            <div className="p-6 space-y-4">
              {modalAccion.accion === 'aprobar' && modalAccion.traslado.tieneDeuda && !modalAccion.traslado.deudaExonerada && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm font-semibold">
                  Tiene deuda pendiente — requiere exoneración antes de aprobar
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Observación (opcional)</label>
                <textarea rows={3} value={obsAccion} onChange={e => setObsAccion(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Observaciones..." />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button onClick={() => setModalAccion(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
              <button onClick={handleConfirmarAccion}
                disabled={acting || (modalAccion.accion === 'aprobar' && modalAccion.traslado.tieneDeuda && !modalAccion.traslado.deudaExonerada)}
                className={`flex items-center gap-2 text-white text-sm font-bold rounded-xl px-5 py-2 transition-colors disabled:opacity-60 ${modalAccion.accion === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {acting && <SpinnerIcon />}
                {modalAccion.accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrasladosPage;
